// ============================================================================
// MY WORK v2 data layer — the notification-backed read + triage verbs.
//
// get_my_work_v2() returns MY recipient rows (RLS: own rows only) enriched
// with computed urgency, SLA, module/label, actor, payload and awaiting-whom.
// Triage writes go through the sanctioned SECURITY DEFINER RPCs (own-rows
// enforced server-side): notify_triage / notify_delegate / notify_ping_owner /
// escalate_to_coo / notify_comment.
// ============================================================================
import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';
import { errorMessage } from '../../../shared/errors';

export interface MyWorkV2Item {
  recipient_row_id: string;
  notification_id: string;
  event_type: string;
  entity_type: string;
  entity_id: string;
  module: string;
  entity_label: string;
  title: string;
  body: string | null;
  target_url: string | null;
  actor_eid: string | null;
  actor_name: string | null;
  reason: string;
  urgency: string;
  urgency_rank: number;
  sla_due_at: string | null;
  created_at: string;
  age_days: number;
  seen_at: string | null;
  read_at: string | null;
  done_at: string | null;
  snoozed_until: string | null;
  archived_at: string | null;
  item_status: string;
  payload: Record<string, unknown>;
  awaiting_eid: string | null;
  awaiting_name: string | null;
  // CW-I Phase 1a: `dm_eligible` is the interruptive flag dispatch selects on;
  // `reason_muted` is this user's per-reason in_app mute, resolved server-side
  // by notify_reason_muted(). Both are projected by get_my_work_v2 so the bell
  // panel can show the interruptive slice without a second round trip — and so
  // no reason-string allowlist ever comes back to the front end (CW-H #237
  // deleted exactly that bridge).
  dm_eligible: boolean;
  reason_muted: boolean;
}

export const REASON_LABEL: Record<string, string> = {
  assigned: 'Assigned',
  approval: 'Approval',
  mention: 'Mention',
  watching: 'Watching',
  sla_breach: 'SLA breach',
  status_change: 'Status change',
  blocked_on_me: 'Blocked on me',
};

// urgency → dot color (computed tiers from compute_urgency()).
export const URGENCY_DOT: Record<string, string> = {
  blocked: 'bg-st-danger-solid',
  breach: 'bg-st-danger-solid',
  high: 'bg-st-warn-solid',
  elevated: 'bg-st-warn-solid',
  aging: 'bg-st-neutral',
  normal: 'bg-st-neutral',
};

// The row budget inside get_my_work_v2(). It is a SAFETY NET, not a page size:
// since 2026-08-24 the function returns active rows only, and the heaviest
// inbox in the company sits at 238. If a list ever draws this many rows it has
// probably been truncated, and it SAYS SO on screen. Never truncate silently —
// that is the whole defect this run exists to close.
export const MY_WORK_ROW_BUDGET = 500;

// Reasons that are updates rather than tasks. Same split the database already
// makes: get_my_work_base_v3 sets waiting_on_me for exactly the complement of
// this set. ⛔ Do not add a new reason word here — read the existing one.
export const INFORMATIONAL_REASONS = ['watching', 'status_change'];

export function isInformationalReason(reason: string | null | undefined): boolean {
  return INFORMATIONAL_REASONS.includes(reason ?? '');
}

export function useMyWorkV2(): {
  items: MyWorkV2Item[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const { user } = useAuth();
  const [items, setItems] = useState<MyWorkV2Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const { data, error: e } = await supabase.rpc('get_my_work_v2');
        if (cancelled) return;
        if (e) throw e;
        setItems((data ?? []) as MyWorkV2Item[]);
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err, 'Failed to load notifications.'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tick]);

  return { items, isLoading, error, refetch: () => setTick((n) => n + 1) };
}

// ----- Triage verbs ----------------------------------------------------------
export type TriageAction =
  | 'seen'
  | 'read'
  | 'done'
  | 'snooze'
  | 'unsnooze'
  | 'archive'
  | 'reactivate';

export async function triage(
  ids: string[],
  action: TriageAction,
  snoozeUntil?: Date,
): Promise<number> {
  const { data, error } = await supabase.rpc('notify_triage', {
    p_recipient_row_ids: ids,
    p_action: action,
    p_snooze_until: snoozeUntil ? snoozeUntil.toISOString() : undefined,
  });
  if (error) throw error;
  return (data as number) ?? 0;
}

// ⛔ A RESOLVE ACTION THAT QUIETLY DOES NOTHING IS WORSE THAN NONE.
//
// notify_triage filters on `recipient_eid = get_user_employee_id()`, so a
// mis-wired call — a stale id, another employee's row, a row already deleted —
// returns a LOWER ROW COUNT rather than raising. Both callers used to ignore
// the return value entirely and inbox.tsx's quickTriage swallowed the error
// outright (`catch { /* non-fatal */ }`). That silence is the bug, not the
// count.
//
// Every triage the user initiates goes through this instead of `triage()`.
export class TriageShortfallError extends Error {
  readonly affected: number;
  readonly requested: number;
  constructor(affected: number, requested: number) {
    super(
      requested === 1
        ? 'That did not go through — nothing was changed. Reload and try again.'
        : `Only ${affected} of ${requested} could be cleared — nothing else was changed. Reload and try again.`,
    );
    this.name = 'TriageShortfallError';
    this.affected = affected;
    this.requested = requested;
  }
}

export async function triageExact(
  ids: string[],
  action: TriageAction,
  snoozeUntil?: Date,
): Promise<number> {
  const n = await triage(ids, action, snoozeUntil);
  if (n !== ids.length) throw new TriageShortfallError(n, ids.length);
  return n;
}

// ----- Derived-row dismissal ---------------------------------------------------
//
// A derived row — open_requirement, incentive_validation, project_payment_review,
// esign_outstanding — is computed on every read from the underlying tables. There
// is no recipient row to archive, so notify_triage cannot touch it, and until
// 2026-08-25 that meant 58 of the 70 cards on Ben's My Work had exactly one
// button: "Open".
//
// ⛔ Dismissing is NOT a status on the underlying record. It records that THIS
// PERSON does not want THIS ITEM in THEIR list. The row still vanishes on its own
// the moment the underlying data satisfies the rule — that half was always true,
// because the row is computed rather than stored.
export interface DerivedItemRef {
  item_type: string;
  item_id: string;
  title?: string | null;
  deep_link_path?: string | null;
}

export async function dismissDerived(item: DerivedItemRef): Promise<number> {
  const { data, error } = await supabase.rpc('my_work_dismiss', {
    p_item_type: item.item_type,
    p_item_id: item.item_id,
    p_title: item.title ?? undefined,
    p_deep_link_path: item.deep_link_path ?? undefined,
  });
  if (error) throw error;
  return (data as number) ?? 0;
}

export async function restoreDerived(itemType: string, itemId: string): Promise<number> {
  const { data, error } = await supabase.rpc('my_work_restore', {
    p_item_type: itemType,
    p_item_id: itemId,
  });
  if (error) throw error;
  return (data as number) ?? 0;
}

/** Same assertion triageExact makes: a call that affects fewer rows than it asked for raises. */
export async function dismissDerivedExact(items: DerivedItemRef[]): Promise<number> {
  let n = 0;
  for (const it of items) n += await dismissDerived(it);
  if (n !== items.length) throw new TriageShortfallError(n, items.length);
  return n;
}

export async function delegateEntity(
  entityType: string,
  entityId: string,
  toEid: string,
  note?: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('notify_delegate', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_to_eid: toEid,
    p_note: note ?? undefined,
  });
  if (error) throw error;
  return data as string;
}

export async function pingOwner(
  entityType: string,
  entityId: string,
  note?: string,
): Promise<void> {
  const { error } = await supabase.rpc('notify_ping_owner', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_note: note ?? undefined,
  });
  if (error) throw error;
}

// ⛔ Escalation has ONE destination and it is the COO seat — never a named
// person, and never the CEO (Ben, 2026-09-02: "ALWAYS ESCALATE TO COO. THE COO
// will ask bryan, not the app."). The app does not decide when the CEO gets
// involved; the COO does, out of band. The seat is resolved from
// tcs_information.coo_eid inside the RPC, which RAISES if it is not set rather
// than falling back to anybody. TCSD-0160.
export async function escalateToCoo(
  entityType: string,
  entityId: string,
  note?: string,
): Promise<void> {
  const { error } = await supabase.rpc('escalate_to_coo', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_note: note ?? undefined,
  });
  if (error) throw error;
}

export async function commentOnEntity(
  entityType: string,
  entityId: string,
  comment: string,
): Promise<void> {
  const { error } = await supabase.rpc('notify_comment', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_comment: comment,
  });
  if (error) throw error;
}

// ----- Detail readers (Command Center) ---------------------------------------
export interface EntityWatcher {
  subscriber_eid: string;
  display_name: string | null;
  reason: string;
  muted: boolean;
}

export interface EntityActivity {
  at: string;
  kind: string;
  actor_eid: string | null;
  actor_name: string | null;
  detail: string | null;
}

export async function fetchWatchers(
  entityType: string,
  entityId: string,
): Promise<EntityWatcher[]> {
  const { data, error } = await supabase.rpc('get_entity_watchers', {
    p_entity_type: entityType,
    p_entity_id: entityId,
  });
  if (error) throw error;
  return (data ?? []) as EntityWatcher[];
}

export async function fetchActivity(
  entityType: string,
  entityId: string,
): Promise<EntityActivity[]> {
  const { data, error } = await supabase.rpc('get_entity_activity', {
    p_entity_type: entityType,
    p_entity_id: entityId,
  });
  if (error) throw error;
  return (data ?? []) as EntityActivity[];
}

// Employee options for the delegate picker.
//
// ⛔ THIS READ v_am_name UNTIL 2026-08-26, AND v_am_name IS A RESOLVER. It
// returns every employee at every status on purpose — closed facilities and old
// routes must still show who ran them — so this picker was offering all seven
// former employees as people you could delegate work to. Pickers bind to
// v_employee_picker, which is filtered to Active. Resolvers resolve; pickers
// exclude.
export interface EmployeeOption {
  employee_id: string;
  display_name: string | null;
}

export async function fetchEmployeeOptions(): Promise<EmployeeOption[]> {
  const { data, error } = await supabase
    .from('v_employee_picker')
    .select('employee_id, display_name')
    .order('display_name');
  if (error) throw error;
  return (data ?? []) as EmployeeOption[];
}

// Mute / unmute a watch (own subscription row; RLS enforces).
export async function setWatchMuted(
  myEid: string,
  entityType: string,
  entityId: string,
  muted: boolean,
): Promise<void> {
  const { error } = await supabase
    .from('subscriptions')
    .upsert(
      {
        subscriber_eid: myEid,
        entity_type: entityType,
        entity_id: entityId,
        reason: 'manual',
        muted,
      },
      { onConflict: 'subscriber_eid,entity_type,entity_id' },
    );
  if (error) throw error;
}
